import React, { useEffect, useMemo, useState } from 'react'
import { Check, GripVertical, MousePointerClick } from 'lucide-react'
import { apiPatch, apiPost, apiPut } from '../../api'
import { Alert, Button } from '../../components/ui'
import {
  chunkTeamsIntoGroups,
  firstKnockoutRound,
  flattenGroupOrder,
  groupLetter,
  groupStageComplete,
  hasKnockoutBracket,
  knockoutMatches,
  resolveGroupSize,
  resolveTeamsAdvancing,
  roundLabel,
  tablesFor
} from '../tournamentStandings'
import {
  FORMAT_LABELS,
  KnockoutExtras,
  MatchScoreList,
  StandingsTable,
  TeamLabel,
  buildScoreBody,
  matchScoreText,
  readError,
  winnerSide
} from './shared'

function SetupTab({ tournament, canManage, saving, run, onReload, success }) {
  const format = tournament.format

  return (
    <div className="stack-lg">
      {format === 'GROUPS_THEN_KNOCKOUT' && (
        <GroupsSetup
          tournament={tournament}
          canManage={canManage}
          saving={saving}
          run={run}
          onReload={onReload}
          success={success}
        />
      )}

      {format === 'KNOCKOUT' && (
        <KnockoutSetup
          tournament={tournament}
          canManage={canManage}
          saving={saving}
          run={run}
          onReload={onReload}
        />
      )}

      {format === 'ROUND_ROBIN' && (
        <RoundRobinSetup
          tournament={tournament}
          canManage={canManage}
          saving={saving}
          run={run}
          onReload={onReload}
        />
      )}

      {!['GROUPS_THEN_KNOCKOUT', 'KNOCKOUT', 'ROUND_ROBIN'].includes(format) && <FormatIntro tournament={tournament} />}
    </div>
  )
}

function FormatIntro({ tournament }) {
  const format = tournament.format
  return (
    <section className="card card-ink tn-format-card" aria-label="Format">
      <span className="tn-format-eyebrow">Format</span>
      <h2 className="tn-format-title">{FORMAT_LABELS[format] || format}</h2>
      <p className="tn-format-help">
        {format === 'GROUPS_THEN_KNOCKOUT' &&
          `Dra lag mellom grupper for å organisere seed. Tabeller oppdateres fra ferdige kamper. Når gruppespillet er ferdig, fylles cupen automatisk med topp ${resolveTeamsAdvancing(tournament)} fra hver gruppe.`}
        {format === 'KNOCKOUT' &&
          'Dra for å endre seeding (1 møter 2, osv.). Rediger resultater i panelet til høyre — vinnere flyttes automatisk videre.'}
        {format === 'ROUND_ROBIN' &&
          'Seriespill: alle møter alle. Se tabellen under og registrer resultater under Kamper eller i oppsettet.'}
      </p>
    </section>
  )
}

function GroupsSetup({ tournament, canManage, saving, run, onReload, success }) {
  const groupSize = resolveGroupSize(tournament)
  const teamsAdvancing = resolveTeamsAdvancing(tournament)
  const [localGroups, setLocalGroups] = useState(() => chunkTeamsIntoGroups(tournament.teams, groupSize))
  const [dragTeamId, setDragTeamId] = useState(null)
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [editGroupSize, setEditGroupSize] = useState(groupSize)
  const [editAdvancing, setEditAdvancing] = useState(teamsAdvancing)
  const tables = useMemo(() => tablesFor(tournament), [tournament])
  const groupsDone = groupStageComplete(tournament)
  const hasKo = hasKnockoutBracket(tournament)
  const koFirst = firstKnockoutRound(tournament)

  useEffect(() => {
    setLocalGroups(chunkTeamsIntoGroups(tournament.teams, resolveGroupSize(tournament)))
  }, [tournament.teams, tournament.groupSize]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEditGroupSize(groupSize)
    setEditAdvancing(teamsAdvancing)
  }, [groupSize, teamsAdvancing])

  useEffect(() => {
    if (!selectedMatch) return
    const fresh = (tournament.matches || []).find((m) => m.id === selectedMatch.id)
    if (fresh) setSelectedMatch(fresh)
  }, [tournament.matches]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveGroupSettings = () => {
    const gs = Math.min(16, Math.max(2, Number(editGroupSize) || 4))
    const ta = Math.min(gs, Math.max(1, Number(editAdvancing) || 2))
    run(async () => {
      const res = await apiPatch(`/api/tournaments/${tournament.id}`, {
        groupSize: gs,
        teamsAdvancingPerGroup: ta
      })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre gruppeoppsett'))
      return { tournament: await res.json() }
    }, 'Gruppeoppsett lagret')
  }

  const persistOrder = (groups) => {
    const teamIds = flattenGroupOrder(groups)
    run(async () => {
      const res = await apiPut(`/api/tournaments/${tournament.id}/teams/order`, { teamIds })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre grupper'))
      return { tournament: await res.json() }
    }, 'Grupper lagret')
  }

  const moveTeam = (teamId, toGroupIdx, toIndex) => {
    if (!canManage || saving) return
    setLocalGroups((prev) => {
      const next = prev.map((g) => [...g])
      let team = null
      for (let g = 0; g < next.length; g++) {
        const idx = next[g].findIndex((t) => t.id === teamId)
        if (idx >= 0) {
          ;[team] = next[g].splice(idx, 1)
          break
        }
      }
      if (!team) return prev
      while (next.length <= toGroupIdx) next.push([])
      const insertAt = Math.min(Math.max(0, toIndex), next[toGroupIdx].length)
      next[toGroupIdx].splice(insertAt, 0, team)
      let lastFilled = -1
      for (let i = 0; i < next.length; i++) {
        if (next[i].length > 0) lastFilled = i
      }
      const safe = lastFilled < 0 ? [[team]] : next.slice(0, lastFilled + 1)
      queueMicrotask(() => persistOrder(safe))
      return safe
    })
  }

  const onDropOnGroup = (e, groupIdx) => {
    e.preventDefault()
    const teamId = e.dataTransfer.getData('text/team-id') || dragTeamId
    if (!teamId || !canManage || saving) return
    moveTeam(teamId, groupIdx, localGroups[groupIdx]?.length || 0)
    setDragTeamId(null)
  }

  const advanceKnockout = () => {
    run(async () => {
      const res = await apiPost(`/api/tournaments/${tournament.id}/advance-knockout`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke generere knockout'))
      return { tournament: await res.json() }
    }, 'Knockout generert')
  }

  const groupSettingsSaved = success === 'Gruppeoppsett lagret'

  return (
    <>
      <div className="tn-two-col stretch">
        <FormatIntro tournament={tournament} />
        {canManage && (
          <section className="card tn-card" aria-labelledby="tn-go-h">
            <div className="tn-head-row center">
              <h2 id="tn-go-h" className="section-title">Gruppeoppsett</h2>
              {groupSettingsSaved && (
                <span role="status" className="pill pill-open pill-lg">
                  <Check size={14} strokeWidth={2.6} aria-hidden="true" />Lagret
                </span>
              )}
            </div>
            <div className="tn-edit-fields">
              <label className="field tn-grow-1">
                <span className="field-label">Lag per gruppe</span>
                <input
                  type="number"
                  className="input-mono"
                  min={2}
                  max={16}
                  disabled={saving}
                  value={editGroupSize}
                  onChange={(e) => {
                    const next = Number(e.target.value) || 4
                    setEditGroupSize(next)
                    if (editAdvancing > next) setEditAdvancing(next)
                  }}
                />
              </label>
              <label className="field tn-grow-1">
                <span className="field-label">Videre per gruppe</span>
                <input
                  type="number"
                  className="input-mono"
                  min={1}
                  max={editGroupSize || 16}
                  disabled={saving}
                  value={editAdvancing}
                  onChange={(e) => setEditAdvancing(Number(e.target.value) || 1)}
                />
              </label>
              <Button
                className="tn-grow-2 tn-field-btn"
                disabled={saving || (editGroupSize === groupSize && editAdvancing === teamsAdvancing)}
                onClick={saveGroupSettings}
              >
                {saving ? 'Lagrer…' : 'Lagre gruppeoppsett'}
              </Button>
            </div>
            <span className="tn-footnote">
              Lag per gruppe: 2–16. Endring regenererer kampoppsett hvis turneringen ikke har startet.
            </span>
          </section>
        )}
      </div>

      <section className="card tn-card" aria-labelledby="tn-gr-h">
        <div className="tn-head-row">
          <h2 id="tn-gr-h" className="section-title">Grupper</h2>
          <span className="tn-footnote">
            {groupSize} lag per gruppe · topp {teamsAdvancing} videre · seed-rekkefølge
          </span>
        </div>
        <p className="tn-footnote">
          Dra lag mellom kolonnene. Ved lagring fordeles lagene i grupper à {groupSize} etter den nye rekkefølgen.
        </p>
        <div className="tn-group-grid">
          {(localGroups.length ? localGroups : [[]]).map((group, gi) => (
            <div
              key={gi}
              className={`tn-group-col ${dragTeamId ? 'drop-ready' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDropOnGroup(e, gi)}
            >
              <div className="tn-group-head">
                <span className="eyebrow">Gruppe {groupLetter(gi + 1)}</span>
                <span className="tn-footnote">{group.length} lag</span>
              </div>
              <ul className="tn-dnd-list">
                {group.map((team, ti) => (
                  <li
                    key={team.id}
                    className={`tn-dnd-item ${dragTeamId === team.id ? 'dragging' : ''} ${canManage ? 'can-drag' : ''}`}
                    draggable={canManage && !saving}
                    onDragStart={(e) => {
                      setDragTeamId(team.id)
                      e.dataTransfer.setData('text/team-id', team.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => setDragTeamId(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const id = e.dataTransfer.getData('text/team-id') || dragTeamId
                      if (id) moveTeam(id, gi, ti)
                      setDragTeamId(null)
                    }}
                  >
                    {canManage && <GripVertical size={16} strokeWidth={1.9} className="tn-dnd-handle" aria-hidden="true" />}
                    <span className="tn-seed">#{team.seed}</span>
                    <span className="tn-dnd-name">{team.name}</span>
                  </li>
                ))}
                {(group.length === 0 || (dragTeamId && !group.some((t) => t.id === dragTeamId))) && (
                  <li className="tn-dnd-empty">Slipp lag her</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="stack" aria-labelledby="tn-tb-h">
        <h2 id="tn-tb-h" className="section-title">Tabeller</h2>
        {tables.length === 0 ? (
          <p className="tn-empty-text">Ingen tabell ennå.</p>
        ) : (
          <div className="tn-tables-grid">
            {tables.map((t) => (
              <StandingsTable key={t.groupNumber} title={t.title} rows={t.rows} advancing={teamsAdvancing} framed />
            ))}
          </div>
        )}
      </section>

      <section className="tn-ko-layout" aria-labelledby="tn-ko-h">
        <div className="card tn-card tn-ko-main">
          <div className="tn-head-row center">
            <h2 id="tn-ko-h" className="section-title">Knockout</h2>
            {hasKo && <span className="tn-footnote">Klikk på en kamp for å registrere resultat</span>}
          </div>
          {hasKo && (
            <KnockoutBracket
              tournament={tournament}
              matches={koFirst}
              allKo={knockoutMatches(tournament)}
              selectedMatch={selectedMatch}
              onSelect={setSelectedMatch}
            />
          )}
          {!hasKo && (
            <div className="tn-dashed-note">
              <span>
                {!groupsDone && (
                  <>
                    <b>Før sluttspillet:</b> Knockout fylles automatisk når alle gruppekamper er ferdigspilt (topp {teamsAdvancing} per gruppe).
                  </>
                )}
                {groupsDone && 'Gruppespillet er ferdig. Trykk «Generer knockout» hvis den ikke ble opprettet automatisk.'}
              </span>
              {canManage && groupsDone && (
                <Button variant="secondary" size="sm" disabled={saving} onClick={advanceKnockout}>
                  Generer knockout
                </Button>
              )}
            </div>
          )}
        </div>
        {hasKo && (
          <ResultEditPanel
            tournament={tournament}
            match={selectedMatch}
            canManage={canManage}
            saving={saving}
            run={run}
            onReload={onReload}
            onClose={() => setSelectedMatch(null)}
          />
        )}
      </section>

      <GroupMatchesQuick
        tournament={tournament}
        canManage={canManage}
        saving={saving}
        run={run}
        onReload={onReload}
      />
    </>
  )
}

function GroupMatchesQuick({ tournament, canManage, saving, run, onReload }) {
  const groupMatches = useMemo(() => {
    const size = resolveGroupSize(tournament)
    const gc = Math.ceil((tournament.teams || []).length / size) || 0
    return (tournament.matches || [])
      .filter((m) => m.roundNumber >= 1 && m.roundNumber <= gc)
      .sort((a, b) => a.roundNumber - b.roundNumber || a.matchNumber - b.matchNumber)
  }, [tournament])

  if (!groupMatches.length) return null

  return (
    <section className="card tn-card" aria-labelledby="tn-gk-h">
      <h2 id="tn-gk-h" className="section-title">Gruppekamper</h2>
      <MatchScoreList
        tournament={tournament}
        matches={groupMatches}
        canManage={canManage}
        saving={saving}
        run={run}
        onReload={onReload}
        groupByRound
        grid
      />
    </section>
  )
}

function KnockoutSetup({ tournament, canManage, saving, run, onReload }) {
  const [seedOrder, setSeedOrder] = useState(() =>
    [...(tournament.teams || [])].sort((a, b) => a.seed - b.seed)
  )
  const [dragId, setDragId] = useState(null)
  const [selectedMatch, setSelectedMatch] = useState(null)
  const firstRound = firstKnockoutRound(tournament)
  const allKo = knockoutMatches(tournament)
  const seedOf = useMemo(() => {
    const map = new Map()
    for (const t of tournament.teams || []) map.set(t.id, t.seed)
    return map
  }, [tournament.teams])

  useEffect(() => {
    setSeedOrder([...(tournament.teams || [])].sort((a, b) => a.seed - b.seed))
  }, [tournament.teams])

  useEffect(() => {
    if (!selectedMatch) return
    const fresh = (tournament.matches || []).find((m) => m.id === selectedMatch.id)
    if (fresh) setSelectedMatch(fresh)
  }, [tournament.matches]) // eslint-disable-line react-hooks/exhaustive-deps

  const persistSeeds = (ordered) => {
    run(async () => {
      const res = await apiPut(`/api/tournaments/${tournament.id}/teams/order`, {
        teamIds: ordered.map((t) => t.id)
      })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre seeding'))
      return { tournament: await res.json() }
    }, 'Seeding lagret')
  }

  const reorder = (fromId, toIndex) => {
    if (!canManage || saving) return
    setSeedOrder((prev) => {
      const next = [...prev]
      const from = next.findIndex((t) => t.id === fromId)
      if (from < 0) return prev
      const [item] = next.splice(from, 1)
      next.splice(toIndex, 0, item)
      queueMicrotask(() => persistSeeds(next))
      return next
    })
  }

  return (
    <>
      <FormatIntro tournament={tournament} />
      <div className="tn-two-col">
        <section className="card tn-card" aria-labelledby="tn-seed-h">
          <div className="tn-head-row">
            <h2 id="tn-seed-h" className="section-title">Seeding</h2>
            <span className="tn-footnote">Dra for å endre rekkefølge</span>
          </div>
          <p className="tn-footnote">
            Dra lag for å endre rekkefølge. Seed 1 spiller mot seed 2 i første runde (etter bracket-utfylling).
          </p>
          <ul className="tn-dnd-list">
            {seedOrder.map((team, i) => (
              <li
                key={team.id}
                className={`tn-dnd-item chalk ${dragId === team.id ? 'dragging' : ''} ${canManage ? 'can-drag' : ''}`}
                draggable={canManage && !saving}
                onDragStart={(e) => {
                  setDragId(team.id)
                  e.dataTransfer.setData('text/team-id', team.id)
                }}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const id = e.dataTransfer.getData('text/team-id') || dragId
                  if (id) reorder(id, i)
                  setDragId(null)
                }}
              >
                {canManage && <GripVertical size={16} strokeWidth={1.9} className="tn-dnd-handle" aria-hidden="true" />}
                <span className="tn-seed">#{i + 1}</span>
                <span className="tn-dnd-name">{team.name}</span>
              </li>
            ))}
            {seedOrder.length === 0 && <li className="tn-empty-text">Ingen lag ennå.</li>}
          </ul>
        </section>

        <section className="card tn-card" aria-labelledby="tn-fk-h">
          <h2 id="tn-fk-h" className="section-title">Første kamper</h2>
          {firstRound.length === 0 ? (
            <p className="tn-empty-text">Ingen kamper. Generer kampoppsett under Innstillinger.</p>
          ) : (
            <div>
              {firstRound.map((m) => (
                <div key={m.id} className="tn-first-match">
                  <span className="tn-first-side home">
                    <TeamLabel name={m.homeTeamName} subtitle={m.homeTeamSubtitle} align="right" />
                    {seedOf.has(m.homeTeamId) && <span className="tn-seed">#{seedOf.get(m.homeTeamId)}</span>}
                  </span>
                  <span className="tn-first-vs">{matchScoreText(m) || 'vs'}</span>
                  <span className="tn-first-side">
                    {seedOf.has(m.awayTeamId) && <span className="tn-seed">#{seedOf.get(m.awayTeamId)}</span>}
                    <TeamLabel name={m.awayTeamName} subtitle={m.awayTeamSubtitle} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {firstRound.length > 0 && (
        <section className="tn-ko-layout" aria-labelledby="tn-cup-h">
          <div className="card tn-card tn-ko-main">
            <div className="tn-head-row center">
              <h2 id="tn-cup-h" className="section-title">Sluttspill</h2>
              <span className="tn-footnote">Klikk på en kamp for å registrere resultat</span>
            </div>
            <KnockoutBracket
              tournament={tournament}
              matches={firstRound}
              allKo={allKo}
              selectedMatch={selectedMatch}
              onSelect={setSelectedMatch}
            />
          </div>
          <ResultEditPanel
            tournament={tournament}
            match={selectedMatch}
            canManage={canManage}
            saving={saving}
            run={run}
            onReload={onReload}
            onClose={() => setSelectedMatch(null)}
          />
        </section>
      )}
    </>
  )
}

function KnockoutBracket({ tournament, matches, allKo, selectedMatch, onSelect }) {
  const rounds = useMemo(() => {
    const nums = [...new Set((allKo || matches).map((m) => m.roundNumber))].sort((a, b) => a - b)
    return nums.map((r) => ({
      round: r,
      label: roundLabel(tournament, r),
      matches: (allKo || matches).filter((m) => m.roundNumber === r).sort((a, b) => a.matchNumber - b.matchNumber)
    }))
  }, [allKo, matches, tournament])

  return (
    <div className="tn-bracket-scroll">
      <div
        className="tn-bracket"
        style={{ gridTemplateColumns: `repeat(${rounds.length}, minmax(200px, 1fr))`, minWidth: rounds.length * 216 }}
      >
        {rounds.map((r) => (
          <div key={r.round} className="tn-bracket-col">
            <span className="eyebrow">{r.label}</span>
            <div className="tn-bracket-matches">
              {r.matches.map((m) => {
                const win = winnerSide(m)
                const selected = selectedMatch?.id === m.id
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`tn-ko-match ${selected ? 'selected' : ''} ${m.status === 'COMPLETED' ? 'done' : ''}`}
                    aria-pressed={selected}
                    onClick={() => onSelect(m)}
                  >
                    <span className={`tn-ko-side ${win === 'home' ? 'winner' : ''}`}>
                      <TeamLabel name={m.homeTeamName} subtitle={m.homeTeamSubtitle} />
                      <span className="tn-ko-score">
                        {m.homeScore != null ? m.homeScore : '–'}
                        {m.homePenaltyScore != null && <small> ({m.homePenaltyScore})</small>}
                      </span>
                    </span>
                    <span className={`tn-ko-side ${win === 'away' ? 'winner' : ''}`}>
                      <TeamLabel name={m.awayTeamName} subtitle={m.awayTeamSubtitle} />
                      <span className="tn-ko-score">
                        {m.awayScore != null ? m.awayScore : '–'}
                        {m.awayPenaltyScore != null && <small> ({m.awayPenaltyScore})</small>}
                      </span>
                    </span>
                    {(m.homePenaltyScore != null || m.extraTime) && (
                      <span className="tn-ko-note">
                        {m.homePenaltyScore != null ? 'Avgjort på straffer' : 'Etter ekstraomganger'}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ResultEditPanel({ tournament, match, canManage, saving, run, onReload, onClose }) {
  const [home, setHome] = useState('')
  const [away, setAway] = useState('')
  const [extras, setExtras] = useState({ penHome: '', penAway: '', extraTime: false })
  const [formError, setFormError] = useState(null)

  useEffect(() => {
    if (!match) return
    setHome(match.homeScore != null ? String(match.homeScore) : '')
    setAway(match.awayScore != null ? String(match.awayScore) : '')
    setExtras({
      penHome: match.homePenaltyScore != null ? String(match.homePenaltyScore) : '',
      penAway: match.awayPenaltyScore != null ? String(match.awayPenaltyScore) : '',
      extraTime: !!match.extraTime
    })
    setFormError(null)
  }, [match])

  if (!match) {
    return (
      <aside className="tn-result-panel idle" aria-labelledby="tn-res-h">
        <MousePointerClick size={24} strokeWidth={1.9} aria-hidden="true" className="muted" />
        <h2 id="tn-res-h" className="section-title">Resultat</h2>
        <p className="tn-footnote">Velg en kamp for å redigere resultat</p>
      </aside>
    )
  }

  const canScore = canManage && match.homeTeamId && match.awayTeamId

  const save = () => {
    const { body, error } = buildScoreBody(match, home, away, extras.penHome, extras.penAway, extras.extraTime)
    setFormError(error || null)
    if (error) return
    run(async () => {
      const res = await apiPut(
        `/api/tournaments/${tournament.id}/matches/${match.id}/score`,
        body
      )
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre resultat'))
      await onReload()
    }, 'Resultat lagret')
  }

  const homeName = match.homeTeamName || 'TBD'
  const awayName = match.awayTeamName || 'TBD'

  return (
    <aside className="tn-result-panel active" aria-labelledby="tn-res-h">
      <div className="tn-head-row center">
        <h2 id="tn-res-h" className="section-title">Resultat</h2>
        <Button variant="tertiary" size="sm" onClick={onClose}>Lukk</Button>
      </div>
      <div className="tn-result-teams">
        <TeamLabel name={match.homeTeamName} subtitle={match.homeTeamSubtitle} />
        <span className="muted">vs</span>
        <TeamLabel name={match.awayTeamName} subtitle={match.awayTeamSubtitle} align="right" />
      </div>
      {canScore ? (
        <>
          <div className="tn-pair">
            <label className="field">
              <span className="field-label tn-small-label">{homeName}</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="tn-score-input lg"
                value={home}
                onChange={(e) => setHome(e.target.value)}
                disabled={saving}
              />
            </label>
            <label className="field">
              <span className="field-label tn-small-label">{awayName}</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="tn-score-input lg"
                value={away}
                onChange={(e) => setAway(e.target.value)}
                disabled={saving}
              />
            </label>
          </div>
          <KnockoutExtras
            match={match}
            home={home}
            away={away}
            penHome={extras.penHome}
            penAway={extras.penAway}
            extraTime={extras.extraTime}
            onChange={(field, value) => setExtras((prev) => ({ ...prev, [field]: value }))}
            disabled={saving}
            hint
          />
          {formError && <Alert tone="error">{formError}</Alert>}
          <Button block disabled={saving} onClick={save}>
            Lagre resultat
          </Button>
        </>
      ) : (
        <p className={match.status === 'COMPLETED' ? 'tn-result-final' : 'tn-footnote'}>
          {match.status === 'COMPLETED'
            ? matchScoreText(match)
            : 'Begge lag må være satt før resultat kan registreres.'}
        </p>
      )}
    </aside>
  )
}

function RoundRobinSetup({ tournament, canManage, saving, run, onReload }) {
  const tables = useMemo(() => tablesFor(tournament), [tournament])
  const matches = tournament.matches || []

  return (
    <>
      <FormatIntro tournament={tournament} />
      <div className="tn-two-col">
        <section className="card tn-card tn-card-flush" aria-labelledby="tn-lt-h">
          <h2 id="tn-lt-h" className="section-title tn-flush-title">Tabell</h2>
          {tables.length === 0 && <p className="tn-empty-text tn-flush-pad">Ingen tabell ennå.</p>}
          {tables.map((t) => (
            <StandingsTable key={t.groupNumber} rows={t.rows} />
          ))}
        </section>
        <section className="card tn-card" aria-labelledby="tn-lk-h">
          <h2 id="tn-lk-h" className="section-title">Kamper</h2>
          <MatchScoreList
            tournament={tournament}
            matches={matches}
            canManage={canManage}
            saving={saving}
            run={run}
            onReload={onReload}
            groupByRound
          />
        </section>
      </div>
    </>
  )
}

export default SetupTab
